import React from 'react';
import {
  RESOLUTION_PRESETS,
  CUSTOM_RESOLUTION,
  STREAM_QUALITY_OPTIONS,
  SESSION_SCOPED_KEYS,
  splitResolutionValue,
  joinResolutionValue,
  type StreamConfig,
} from '../utils/streamConfig';
import styles from './StreamConfigFields.module.css';

interface StreamConfigFieldsProps {
  value: StreamConfig;
  onChange: (next: StreamConfig) => void;
  /**
   * Mark options that need a new session. The share dialog builds a session
   * from scratch so nothing is restricted there; the preview panel is editing a
   * live one and says so.
   */
  markSessionScoped?: boolean;
}

/** A resolution select plus the custom W×H boxes it reveals. */
const ResolutionField: React.FC<{
  id: string;
  label: string;
  hint: string;
  emptyLabel: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}> = ({ id, label, hint, emptyLabel, value, onChange }) => {
  const { preset, width, height } = splitResolutionValue(value);

  // Custom dimensions live in the committed value, so a half-typed pair has
  // nowhere to sit — hold them locally and commit only a complete pair.
  const [draftW, setDraftW] = React.useState(width);
  const [draftH, setDraftH] = React.useState(height);

  // Custom mode is DERIVED from the value, with one exception: an incomplete
  // pair commits `undefined`, which is indistinguishable from "None". The
  // sticky flag covers exactly that window and nothing else, so an external
  // change (Reset, or loading a share record) still wins — deriving it purely
  // from state would strand the boxes open after a reset to a named preset.
  const [customSticky, setCustomSticky] = React.useState(preset === CUSTOM_RESOLUTION);
  const isCustom = preset === CUSTOM_RESOLUTION || (customSticky && !preset);

  React.useEffect(() => {
    const parsed = splitResolutionValue(value);
    if (parsed.preset === CUSTOM_RESOLUTION) {
      setDraftW(parsed.width);
      setDraftH(parsed.height);
    } else if (parsed.preset) {
      setCustomSticky(false);
    }
  }, [value]);

  const selectValue = isCustom ? CUSTOM_RESOLUTION : preset;

  const commitCustom = (w: string, h: string) => {
    onChange(joinResolutionValue(CUSTOM_RESOLUTION, w, h));
  };

  return (
    <div className={styles.formGroup}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      <select
        id={id}
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === CUSTOM_RESOLUTION) {
            setCustomSticky(true);
            commitCustom(draftW, draftH);
          } else {
            setCustomSticky(false);
            onChange(v || undefined);
          }
        }}
        className={styles.select}
      >
        <option value="">{emptyLabel}</option>
        {RESOLUTION_PRESETS.map((p) => (
          <option key={p.value} value={p.value}>{p.label}</option>
        ))}
        <option value={CUSTOM_RESOLUTION}>Custom…</option>
      </select>

      {isCustom && (
        <div className={styles.dimensionRow}>
          <input
            type="number"
            min={64}
            step={2}
            inputMode="numeric"
            placeholder="Width"
            aria-label={`${label} width`}
            value={draftW}
            onChange={(e) => { setDraftW(e.target.value); commitCustom(e.target.value, draftH); }}
            className={styles.dimensionInput}
          />
          <span className={styles.dimensionSeparator} aria-hidden="true">×</span>
          <input
            type="number"
            min={64}
            step={2}
            inputMode="numeric"
            placeholder="Height"
            aria-label={`${label} height`}
            value={draftH}
            onChange={(e) => { setDraftH(e.target.value); commitCustom(draftW, e.target.value); }}
            className={styles.dimensionInput}
          />
        </div>
      )}

      <span className={styles.hint}>{hint}</span>
    </div>
  );
};

export const StreamConfigFields: React.FC<StreamConfigFieldsProps> = ({
  value,
  onChange,
  markSessionScoped = false,
}) => {
  const set = <K extends keyof StreamConfig>(key: K, next: StreamConfig[K]) =>
    onChange({ ...value, [key]: next });

  const scopeNote = (key: keyof StreamConfig) =>
    markSessionScoped && SESSION_SCOPED_KEYS.includes(key)
      ? <span className={styles.scopeTag}>reconnects</span>
      : null;

  const toggle = (key: keyof StreamConfig, label: string, hint: string) => (
    <label className={styles.checkboxLabel}>
      <input
        type="checkbox"
        checked={!!value[key]}
        onChange={(e) => set(key, e.target.checked as never)}
        className={styles.checkbox}
      />
      <span className={styles.checkboxText}>{label}{scopeNote(key)}</span>
      <span className={styles.hint}>{hint}</span>
    </label>
  );

  return (
    <div className={styles.fields}>
      {toggle('pointerLock', 'Pointer lock', 'Capture the cursor for mouse-look controls')}
      {toggle('nativeTouch', 'Native touch', 'Send real touch events instead of synthesised mouse')}
      {toggle('suppressBrowserKeys', 'Suppress browser keys', 'Stop F-keys and Ctrl shortcuts reaching the browser')}
      {toggle('useMic', 'Microphone', 'Enable microphone input (requires secure context)')}
      {toggle('forceRelay', 'Force TURN relay', 'Route media through TURN — use to reproduce restricted networks')}

      <div className={styles.formGroup}>
        <label htmlFor="stream-quality-select" className={styles.label}>
          Stream quality{scopeNote('streamQuality')}
        </label>
        <select
          id="stream-quality-select"
          value={value.streamQuality ?? ''}
          onChange={(e) => set('streamQuality', e.target.value || undefined)}
          className={styles.select}
        >
          <option value="">Default (cinematic)</option>
          {STREAM_QUALITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className={styles.hint}>Bitrate allowance and receiver buffering</span>
      </div>

      <ResolutionField
        id="resolution-clamp-select"
        label="Resolution clamp"
        emptyLabel="None (native resolution)"
        hint="Upper bound — scales the viewport down to fit inside this"
        value={value.resolutionClamp}
        onChange={(v) => set('resolutionClamp', v)}
      />

      <ResolutionField
        id="resolution-floor-select"
        label="Resolution floor"
        emptyLabel="None (no minimum)"
        hint="Lower bound — scales the viewport up until it covers this. Match it to the clamp to pin a fixed resolution from any viewport; the stream then letterboxes if its shape differs from the element."
        value={value.resolutionFloor}
        onChange={(v) => set('resolutionFloor', v)}
      />
    </div>
  );
};
